import {
  Flex,
  FlexItem,
  FormGroup,
  Select,
  SelectOption,
  SelectVariant,
  TextInput,
  Divider,
  PageSection,
} from '@patternfly/react-core'
import * as React from 'react'
import { useState, useCallback } from 'react'

import {
  constructIsoDuration,
  parseIsoDuration,
} from '@app/domain/program/program.helpers'
import type {
  IProgram,
  IProgramInteraction,
  IProgramInteractionId} from '@app/domain/program/program.interface';
import {
  IInteractionStatusId,
  ProgramInteractionTypeId,
  selectConfigurationForInteractionModalTittle,
} from '@app/domain/program/program.interface'

import { InteractionSection } from './interaction-section'
import { RemoteInteraction } from './remote-interaction'

interface Props {
  program: IProgram
  onDeleteInteraction: (programInteractionId: IProgramInteractionId) => void
  onDeleteReportTemplate: (
    programInteractionId: IProgramInteractionId,
    templateId: string,
  ) => void
  onEditInteraction: (
    modalTitle: string,
    programInteractionTypeId: ProgramInteractionTypeId,
    programInteraction?: IProgramInteraction,
  ) => void
  onEditInteractionProperties: (
    interactionId: string,
    label?: string,
    daysAfterInitialInteraction?: number,
  ) => void
  onEditInteractionReportTemplates: (
    programInteraction?: IProgramInteraction,
  ) => void
  onEditExpirationPeriod: (newExpiration: string) => void
}

const expirationUnits = [
  { label: 'Minutes', value: 'M', type: 'time' },
  { label: 'Hours', value: 'H', type: 'time' },
  { label: 'Days', value: 'D', type: 'date' },
  { label: 'Weeks', value: 'W', type: 'date' },
  { label: 'Months', value: 'MO', type: 'date' },
]

export const RemoteInteractionSection = ({
  onDeleteInteraction,
  onDeleteReportTemplate,
  onEditInteraction,
  onEditInteractionProperties,
  onEditInteractionReportTemplates,
  onEditExpirationPeriod,
  program,
}: Props) => {
  const { quantity, unit, type } = parseIsoDuration(
    program.remoteInteractionExpirationPeriod,
  )
  const [expirationQuantity, setExpirationQuantity] = useState<number>(quantity)
  const [expirationUnit, setExpirationUnit] = useState<string>(unit)
  const [expirationType, setExpirationType] = useState<'time' | 'date'>(type)
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false)

  const updateExpiration = (
    newQuantity: number,
    newUnit: string,
    newType: 'time' | 'date',
  ) => {
    setExpirationQuantity(newQuantity)
    setExpirationUnit(newUnit)
    setExpirationType(newType)

    onEditExpirationPeriod(constructIsoDuration(newQuantity, newUnit, newType))
  }

  const handleAddInteraction = useCallback(() => {
    return (event) => {
      event.preventDefault()
      onEditInteraction(
        selectConfigurationForInteractionModalTittle,
        ProgramInteractionTypeId.RemoteInteraction,
      )
    }
  }, [onEditInteraction])

  const renderInteractions = (programInteractions: IProgramInteraction[]) => {
    if (!programInteractions || programInteractions.length === 0) {
      return null
    }

    const activeInteractions = programInteractions.filter(
      (interaction) => interaction.status !== IInteractionStatusId.Removed,
    )

    if (activeInteractions.length === 0) {
      return null
    }

    return activeInteractions.map((programInteraction) => (
      <RemoteInteraction
        key={programInteraction.id}
        onDeleteInteraction={onDeleteInteraction}
        onDeleteReportTemplate={onDeleteReportTemplate}
        onEditInteraction={onEditInteraction}
        onEditInteractionProperties={onEditInteractionProperties}
        onEditInteractionReportTemplates={onEditInteractionReportTemplates}
        programInteraction={programInteraction}
        program={program}
      />
    ))
  }

  return (
    <InteractionSection
      toolTip="Scheduled remote interactions to collect follow-up information on a patient in a program."
      subtitle="Remote Interactions"
      addInteraction={handleAddInteraction()}
    >
      <Divider />
      <PageSection>
        <FormGroup isRequired fieldId="program-expiry">
          <Flex
            spaceItems={{ default: 'spaceItemsMd' }}
            alignItems={{ default: 'alignItemsCenter' }}
          >
            <FlexItem>
              <span>Remote Interaction Expiry Period *</span>
            </FlexItem>

            <FlexItem style={{ maxWidth: '80px' }}>
              <TextInput
                value={expirationQuantity}
                type="number"
                min="1"
                onChange={(value) =>
                  updateExpiration(
                    Number(value),
                    expirationUnit,
                    expirationType,
                  )
                }
                aria-label="Expiration Quantity"
              />
            </FlexItem>

            <FlexItem>
              <Select
                variant={SelectVariant.single}
                isOpen={isUnitDropdownOpen}
                selections={expirationUnit}
                onToggle={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                onSelect={(_, selection) => {
                  const selectedOption =
                    expirationUnits.find(
                      (opt) => opt.value === selection.toString(),
                    ) ?? expirationUnits[0]
                  updateExpiration(
                    expirationQuantity,
                    selection.toString(),
                    selectedOption.type as 'time' | 'date',
                  )
                  setIsUnitDropdownOpen(false)
                }}
                aria-labelledby="expiration-unit-label"
              >
                {expirationUnits.map((option) => (
                  <SelectOption key={option.value} value={option.value}>
                    {option.label}
                  </SelectOption>
                ))}
              </Select>
            </FlexItem>
          </Flex>
        </FormGroup>
        {renderInteractions(
          program?.interactions[ProgramInteractionTypeId.RemoteInteraction],
        )}
      </PageSection>
    </InteractionSection>
  )
}
